import { ModelBadge } from "@/components/ModelBadge";
import { useEffect, useRef, useState } from "react";
import { Play, Pause, DownloadSimple, MusicNotes, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { registerAudioPlay, clearAudioRegistration } from "@/lib/audioManager";
import { API } from "@/lib/apiBase";

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
};

const Processing = ({ song }) => (
  <div className="flex items-center gap-4 py-2" data-testid={`song-processing-${song.version}`}>
    <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
      <div className="w-5 h-5 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
    </div>
    <div>
      <p className="text-white font-medium">{song.song_name}</p>
      <p className="text-xs text-[#A1A1AA]">Creating… usually 2-3 minutes</p>
    </div>
  </div>
);

const Failed = ({ song }) => (
  <div className="flex items-center gap-3 py-2 text-red-400" data-testid={`song-failed-${song.version}`}>
    <WarningCircle size={28} weight="fill" />
    <div>
      <p className="text-white font-medium">{song.song_name}</p>
      <p className="text-xs">{song.failure_reason || "Generation failed"} — 15 credits refunded.</p>
    </div>
  </div>
);

export const SongPlayerCard = ({ song }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(song.duration || 0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration || song.duration || 0);
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onPause);
    a.addEventListener("play", onPlay);
    return () => {
      ["timeupdate", "loadedmetadata", "pause", "ended", "play"].forEach((ev) => a.removeEventListener(ev, ev === "timeupdate" ? onTime : ev === "loadedmetadata" ? onMeta : ev === "play" ? onPlay : onPause));
      clearAudioRegistration(a);
    };
  }, [song.id, song.status, song.duration]);

  if (song.status === "processing") return <Processing song={song} />;
  if (song.status === "failed") return <Failed song={song} />;

  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a.pause(); return; }
    registerAudioPlay(a);
    a.play().catch(() => toast.error("Unable to play. Try downloading instead."));
  };
  const max = duration || 100;

  return (
    <div data-testid={`song-card-${song.version}`}>
      <audio ref={audioRef} src={`${API}/songs/stream/${song.id}`} preload="metadata" />
      <div className="flex items-center gap-4">
        {song.image_url ? (
          <img src={song.image_url} alt={song.song_name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center shrink-0"><MusicNotes size={24} style={{ color: "#F59E0B" }} /></div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate" data-testid={`song-name-${song.version}`}>{song.song_name}</p>
          <ModelBadge song={song} />
          <div className="flex items-center gap-3 mt-2">
            <button onClick={toggle} className="w-9 h-9 rounded-full bg-[#F59E0B] flex items-center justify-center text-black hover:bg-[#D97706] transition-colors shrink-0" data-testid={`play-btn-${song.version}`}>
              {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
            </button>
            <input type="range" min="0" max={max} step="0.1" value={time} onChange={(e) => { audioRef.current.currentTime = parseFloat(e.target.value); }} className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, #F59E0B ${(time / max) * 100}%, rgba(255,255,255,0.1) 0%)` }} data-testid={`seek-${song.version}`} />
            <span className="text-xs text-[#A1A1AA] tabular-nums w-20 text-right">{formatTime(time)} / {formatTime(duration)}</span>
          </div>
        </div>
        <a href={`${API}/songs/download/${song.id}`} className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:text-[#F59E0B] hover:border-[#F59E0B] transition-colors shrink-0" title="Download MP3" data-testid={`download-btn-${song.version}`}>
          <DownloadSimple size={18} weight="bold" />
        </a>
      </div>
    </div>
  );
};
